import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ContentPreview = ({ content, contentType, onSave, onExport, onRegenerate, isGenerating }) => {
  const [editMode, setEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState(content);

  const handleSaveEdit = () => {
    setEditMode(false);
    // In a real app, this would update the content
  };

  const handleCancelEdit = () => {
    setEditedContent(content);
    setEditMode(false);
  };

  const formatContent = () => {
    if (!content) return null;

    switch (contentType) {
      case 'press-release':
        return (
          <div className="prose max-w-none">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-foreground mb-2">FOR IMMEDIATE RELEASE</h2>
              <p className="text-muted-foreground">PenGame Publishing</p>
            </div>
            <div className="whitespace-pre-wrap text-foreground leading-relaxed">
              {content}
            </div>
          </div>
        );
      
      case 'social-media':
        return (
          <div className="space-y-4">
            {content?.platforms?.map((platform, index) => (
              <div key={index} className="border border-border rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="Share2" size={16} className="text-primary" />
                  <span className="font-medium text-foreground capitalize">{platform?.platform}</span>
                  <span className="text-xs text-muted-foreground">
                    {platform?.characterCount} characters
                  </span>
                </div>
                <div className="whitespace-pre-wrap text-foreground">
                  {platform?.content}
                </div>
              </div>
            ))}
          </div>
        );
      
      case 'blog-post':
        return (
          <div className="prose max-w-none">
            <h1 className="text-3xl font-bold text-foreground mb-4">{content?.title}</h1>
            {content?.metaDescription && (
              <div className="bg-muted p-3 rounded-md mb-6">
                <p className="text-sm text-muted-foreground">
                  <strong>Meta Description:</strong> {content?.metaDescription}
                </p>
              </div>
            )}
            <div className="whitespace-pre-wrap text-foreground leading-relaxed">
              {content?.body}
            </div>
          </div>
        );
      
      case 'story-angles':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3">Story Angles</h3>
              <div className="space-y-3">
                {content?.angles?.map((angle, index) => (
                  <div key={index} className="border border-border rounded-lg p-4">
                    <h4 className="font-medium text-foreground mb-2">{angle?.title}</h4>
                    <p className="text-muted-foreground text-sm">{angle?.description}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3">Headlines</h3>
              <div className="space-y-2">
                {content?.headlines?.map((headline, index) => (
                  <div key={index} className="p-3 bg-muted rounded-md">
                    <p className="text-foreground">{headline}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      
      default:
        return (
          <div className="whitespace-pre-wrap text-foreground">
            {typeof content === 'string' ? content : JSON.stringify(content, null, 2)}
          </div>
        );
    }
  };

  if (!content && !isGenerating) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <Icon name="FileText" size={48} className="text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">No Content Generated</h3>
        <p className="text-muted-foreground">
          Fill out the form and click generate to create AI-powered content
        </p>
      </div>
    );
  }

  if (isGenerating) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
        <h3 className="text-lg font-medium text-foreground mb-2">Generating Content...</h3>
        <p className="text-muted-foreground">
          AI is creating your content. This may take a few moments.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Action Bar */}
      <div className="flex items-center justify-between border-b border-border pb-4">
        <div className="flex items-center space-x-2">
          <Icon name="FileText" size={20} className="text-primary" />
          <span className="font-medium text-foreground">Generated Content</span>
        </div>
        <div className="flex items-center space-x-2">
          {!editMode ? (
            <>
              <Button
                variant="outline"
                size="sm"
                iconName="Edit2"
                onClick={() => setEditMode(true)}
              >
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="RefreshCw"
                onClick={onRegenerate}
              >
                Regenerate
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                onClick={onExport}
              >
                Export
              </Button>
              <Button
                size="sm"
                iconName="Save"
                onClick={onSave}
              >
                Save
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancelEdit}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSaveEdit}
              >
                Save Changes
              </Button>
            </>
          )}
        </div>
      </div>
      {/* Content Display */}
      <div className="bg-card border border-border rounded-lg p-6 min-h-[400px]">
        {editMode ? (
          <textarea
            className="w-full h-96 px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none font-mono text-sm"
            value={typeof editedContent === 'string' ? editedContent : JSON.stringify(editedContent, null, 2)}
            onChange={(e) => setEditedContent(e?.target?.value)}
          />
        ) : (
          formatContent()
        )}
      </div>
    </div>
  );
};

export default ContentPreview;